import { type Currency, FundingRound, type Headcount } from "@prisma/client";
import HttpStatus from "http-status-codes";
import { type NextApiHandler } from "next";
import { array, boolean, mixed, number, object, ref, string } from "yup";
import type Reference from "yup/lib/Reference";
import { type ApiRoute } from "~/hooks/useApi";
import { api } from "~/lib/api";
import { AuthenticationError } from "~/lib/errors/authenticationError";
import { trackMarketDataSearched } from "~/lib/external/segment/server/events";
import { createQuery } from "~/lib/reactQuery";
import { type YupOutputType } from "~/lib/utils";
import { type Measure } from "~/services/employee";
import { EmployeeLevel, getAllowedLevels } from "~/services/employee/employeeLevel";
import { getMarketDataStats } from "~/services/market-data/getMarketDataStats";
import { type MarketDataStats } from "~/services/marketCompensationStats";
import { type PercentileBounds } from "~/services/percentiles";

export type GetMarketDataStatsMeasure = Extract<
  Measure,
  "totalCash" | "baseSalary" | "onTargetEarnings" | "onTargetBonus"
>;

export type GetMarketDataStatsResponse = {
  currency: Currency;
  suggestedCurrencies: Currency[];
  percentiles: PercentileBounds;
  market: { byLevel: { [key in EmployeeLevel | string]?: MarketDataStats | null } };
  comparison?: { byLevel: { [key in EmployeeLevel | string]?: MarketDataStats | null } };
};

export const FiltersSchema = object({
  fundingRounds: array()
    .of(mixed<FundingRound>().oneOf(Object.values(FundingRound)).required())
    .ensure(),
  headcount: mixed<Headcount>().nullable(),
  industriesIds: array().of(number().required()).ensure(),
  industryId: number().nullable(),
  jobsIds: array().of(string().required()).min(1).ensure(),
  labelsIds: array().of(number().required()).ensure(),
  locationsIds: array().of(number().required()).ensure(),
  size: object({
    min: number().min(0).nullable(),
    max: number().min(1).nullable(),
  }).required(),
});

export const GetMarketDataStatsSchema = object({
  presetId: number().nullable(),
  useTestData: boolean().required(),
  useBenchmarkLevelsInShare: boolean().nullable(),
  currency: string().optional(),
  measure: mixed<Measure>().required(),
  showCompanyEmployees: boolean().required(),
  levels: array()
    .of(mixed<EmployeeLevel>().oneOf(Object.values(EmployeeLevel)).required())
    .ensure(),
  benchmarkLevelIds: array().of(number().required()).ensure(),
  mergeAdvancedLevels: boolean().required(),
  market: FiltersSchema.required(),
  compare: FiltersSchema.nullable(),
  percentiles: object({
    low: number().min(0.1).required(),
    high: number().max(0.9).required(),
    middle: number()
      .required()
      .min(ref("low") as Reference<number>)
      .max(ref("high") as Reference<number>),
  }).required(),
});

export type MarketOrCompareInput = YupOutputType<typeof FiltersSchema>;
export type Input = Omit<YupOutputType<typeof GetMarketDataStatsSchema>, "compare" | "market"> & {
  compare: MarketOrCompareInput | null;
  market: MarketOrCompareInput;
};

const handler: NextApiHandler<GetMarketDataStatsResponse | null> = async (req, res) => {
  const input = GetMarketDataStatsSchema.validateSync(req.body);

  if (!req.user && !input.useTestData) {
    res.status(HttpStatus.UNAUTHORIZED);
    throw new AuthenticationError("Unauthorised: user must be authenticated or use test data");
  }
  if (!req.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS) {
    const allowedLevels = getAllowedLevels(req.user, input.mergeAdvancedLevels);

    const serverSchema = object({ levels: array().of(string().oneOf(allowedLevels).required()).ensure() });
    serverSchema.validateSync(req.body);
  }

  await trackMarketDataSearched(req, { input: input, origin: "INPUT_UPDATE" });
  const stats = await getMarketDataStats(req, input, req.user);

  res.setHeader("Cache-Control", "s-maxage=1, stale-while-revalidate");
  res.status(HttpStatus.OK).json(stats);
};

export default api(handler, {
  method: "POST",
  authentication: { optional: true },
});

export const GET_MARKET_DATA_STATS_QUERY_KEY: ApiRoute["pathname"] = "/api/get-market-data-stats";

export const useGetMarketDataStatsQuery = createQuery<typeof handler, typeof GetMarketDataStatsSchema>({
  path: GET_MARKET_DATA_STATS_QUERY_KEY,
  schema: GetMarketDataStatsSchema,
});
